import type { WasteEvent } from "@/hooks/use-waste-events";
import { isoDowInTz } from "@/lib/heatmap";

/**
 * The Proof feed filter (POS2-868): what the capture feed is narrowed to.
 * `line` comes from the Pulse strip (POS2-871, `key` matches `lineKey(event)`);
 * `cell` comes from the heatmap hot cell (POS2-873, a service + site-local
 * day-of-week). `label` renders the chip either way.
 */
export type ProofFilter =
  | { kind: "line"; key: string; label: string }
  | { kind: "cell"; service: string; isoDow: number; label: string };

function matchesFilter(event: WasteEvent, filter: ProofFilter): boolean {
  if (filter.kind === "line") return lineKey(event) === filter.key;
  return (
    (event.service_time ?? "unassigned") === filter.service &&
    isoDowInTz(event.logged_at, event.timezone) === filter.isoDow
  );
}

/**
 * Identity of the waste "line" an event belongs to. The key format is the
 * canonical one produced by the POS2-867 `waste_event_line_stats` RPC — the
 * shared spine feeding Pulse, What's-working, Examine, and The Proof — so that
 * a line selected from line-stats matches events here by string equality:
 * recipe → inventory item → subcategory → category → 'unidentified'.
 * Used to dedupe The Proof to one card per line and to match the feed filter.
 */
export function lineKey(event: WasteEvent): string {
  if (event.recipe_id) return `recipe:${event.recipe_id}`;
  if (event.inventory_item_id) return `item:${event.inventory_item_id}`;
  if (event.subcategory_id) return `subcategory:${event.subcategory_id}`;
  if (event.category_id) return `category:${event.category_id}`;
  return "unidentified";
}

/**
 * The cards to show: optionally narrowed to a single line, then deduped to one
 * card per line (the first — events arrive pre-sorted by cost desc), then
 * capped at `limit`. Input order is preserved, so "highest value first" is the
 * caller's responsibility (the query sorts by cost_estimate desc).
 */
export function selectProofEvents(
  events: WasteEvent[],
  { filter, limit }: { filter: ProofFilter | null; limit: number },
): WasteEvent[] {
  const scoped = filter ? events.filter((e) => matchesFilter(e, filter)) : events;
  const seen = new Set<string>();
  const out: WasteEvent[] = [];
  for (const e of scoped) {
    const key = lineKey(e);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
    if (out.length === limit) break;
  }
  return out;
}

/**
 * The equivalent previous period: same length, shifted back so it ends where
 * the current one begins. An open-ended `to` (relative presets run up to now)
 * is closed at `now` before measuring the span. Returns nulls when there's no
 * `from` to anchor to (all-time), where a previous period is undefined.
 */
export function previousPeriod(
  range: { from: string | null; to: string | null },
  now: Date,
): { from: string | null; to: string | null } {
  if (!range.from) return { from: null, to: null };
  const from = new Date(range.from).getTime();
  const to = range.to ? new Date(range.to).getTime() : now.getTime();
  const span = Math.max(0, to - from);
  return {
    from: new Date(from - span).toISOString(),
    to: new Date(from).toISOString(),
  };
}
